import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from '../../hooks/use-toast';
import { CheckCircle2, AlertTriangle, XCircle, Download, Filter, Users } from 'lucide-react';
import { apiFetch } from '../../services/api';

const API = process.env.REACT_APP_BACKEND_URL;

const BRANCHEN = [
  { key: 'alle', label: 'Alle Gewerke', emoji: '🏢' },
  { key: 'elektro', label: 'Elektro', emoji: '⚡' },
  { key: 'shk', label: 'SHK', emoji: '🔧' },
  { key: 'holz', label: 'Holz/Tischler', emoji: '🪵' },
  { key: 'metall', label: 'Metall', emoji: '🔩' },
  { key: 'bau', label: 'Bau', emoji: '🧱' },
  { key: 'allgemein', label: 'Allgemein (BetrSichV)', emoji: '📋' },
];

/**
 * SimpleChefDashboard — reduzierte Chef-Ansicht auf 3 Kernbereiche:
 * 1. Ampel-Status (DGUV/BetrSichV-Fälligkeiten)
 * 2. 1-Klick BG-ZIP-Export
 * 3. Branchen-Filter für die Kursauswahl
 */
export const SimpleChefDashboard = ({ employees = [], stats = {}, onNavigateToTeam, onNavigateToCourses }) => {
  const [zipLoading, setZipLoading] = useState(false);
  const [activeBranche, setActiveBranche] = useState('alle');
  const [ampelData, setAmpelData] = useState(null);

  useEffect(() => {
    loadAmpel();
  }, []);

  const loadAmpel = async () => {
    try {
      const res = await apiFetch(`${API}/api/inhaber/ampel-status`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setAmpelData(data);
    } catch {
      /* silent */
    }
  };

  const ampelCounts = useMemo(() => {
    if (ampelData?.counts) return ampelData.counts;
    // Fallback: aus employees ableiten
    const c = { gruen: 0, gelb: 0, rot: 0 };
    for (const e of employees) {
      const s = e.ampel || e.status || 'gruen';
      if (s === 'rot') c.rot += 1;
      else if (s === 'gelb') c.gelb += 1;
      else c.gruen += 1;
    }
    return c;
  }, [ampelData, employees]);

  const totalEmployees = employees.length || stats.totalEmployees || 0;

  const downloadZip = async () => {
    setZipLoading(true);
    try {
      const res = await fetch(`${API}/api/certificates/company/zip`, { credentials: 'include' });
      if (!res.ok) {
        toast({ title: 'Download fehlgeschlagen', description: `Status ${res.status}`, variant: 'destructive' });
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartvolt-zertifikate-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: '✓ ZIP heruntergeladen', description: 'Alle BG-relevanten Zertifikate für den Prüfer.' });
    } catch (e) {
      toast({ title: 'Fehler', description: String(e), variant: 'destructive' });
    } finally {
      setZipLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="simple-chef-dashboard">
      {/* 1. AMPEL-STATUS */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <span className="text-2xl" aria-hidden="true">🚦</span>
            DGUV / BetrSichV Ampel-Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-3">
            <button
              onClick={onNavigateToTeam}
              className="rounded-lg border border-green-500/40 bg-green-500/10 p-4 text-left hover:bg-green-500/15 transition-colors"
              data-testid="ampel-gruen"
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-xs uppercase text-green-300 font-semibold">Alles OK</span>
              </div>
              <div className="text-3xl font-black text-green-400">{ampelCounts.gruen}</div>
              <div className="text-xs text-gray-400 mt-1">Mitarbeiter aktuell</div>
            </button>
            <button
              onClick={onNavigateToTeam}
              className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-4 text-left hover:bg-yellow-500/15 transition-colors"
              data-testid="ampel-gelb"
            >
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-xs uppercase text-yellow-300 font-semibold">Bald fällig</span>
              </div>
              <div className="text-3xl font-black text-yellow-400">{ampelCounts.gelb}</div>
              <div className="text-xs text-gray-400 mt-1">Auffrischung &lt; 30 Tage</div>
            </button>
            <button
              onClick={onNavigateToTeam}
              className="rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-left hover:bg-red-500/15 transition-colors"
              data-testid="ampel-rot"
            >
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-5 h-5 text-red-400" />
                <span className="text-xs uppercase text-red-300 font-semibold">Überfällig</span>
              </div>
              <div className="text-3xl font-black text-red-400">{ampelCounts.rot}</div>
              <div className="text-xs text-gray-400 mt-1">Sofort handeln!</div>
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
            <Users className="w-3.5 h-3.5" />
            <span>Gesamt: {totalEmployees} Mitarbeiter</span>
          </div>
        </CardContent>
      </Card>

      {/* 2. BG-ZERTIFIKATE ZIP-EXPORT */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-500/30">
        <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="p-3 rounded-lg bg-yellow-500/15 border border-yellow-500/30">
              <Download className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h3 className="text-white font-bold text-lg">BG-Zertifikate ZIP-Export</h3>
              <p className="text-gray-400 text-sm">Alle Zertifikate deiner Mitarbeiter als ZIP für den BG-Prüfer</p>
            </div>
          </div>
          <Button
            onClick={downloadZip}
            disabled={zipLoading}
            size="lg"
            className="bg-yellow-500 text-black hover:bg-yellow-400 font-bold shrink-0"
            data-testid="simple-zip-download-btn"
          >
            {zipLoading ? 'Lade…' : '1-Klick Download'}
            <Download className="ml-2 w-4 h-4" />
          </Button>
        </CardContent>
      </Card>

      {/* 3. BRANCHEN-FILTER */}
      <Card className="bg-gradient-to-br from-gray-900 to-black border-yellow-500/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-white flex items-center gap-2">
            <Filter className="w-5 h-5 text-yellow-400" />
            Branchen-Filter — welche Kurse für dein Gewerk?
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2" data-testid="branche-filter-row">
            {BRANCHEN.map((b) => (
              <button
                key={b.key}
                onClick={() => {
                  setActiveBranche(b.key);
                  if (onNavigateToCourses) onNavigateToCourses(b.key);
                }}
                className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all border ${
                  activeBranche === b.key
                    ? 'bg-yellow-500 text-black border-yellow-400'
                    : 'bg-gray-800/50 text-gray-300 border-gray-700 hover:bg-gray-800 hover:border-yellow-500/40'
                }`}
                data-testid={`branche-${b.key}`}
              >
                <span className="mr-1.5">{b.emoji}</span>{b.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">
            Zeigt Kurse gefiltert nach deinem Gewerk — z. B. BetrSichV-Universal + branchenspezifische DGUV-Schulungen.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default SimpleChefDashboard;
